import { useState, useEffect } from "react";
import { X, Filter, ChevronDown, RotateCcw, Save, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

// Job size options - using NORMALIZED tier format for consistency with lead cards
// Backend handles legacy formats, so these display labels will match lead badges
const JOB_SIZE_OPTIONS = [
  { value: "Less than $1,000", label: "$0–$1,000" },
  { value: "$1,000–$5,000", label: "$1,001–$5,000" },
  { value: "$5,000–$10,000", label: "$5,001–$10,000" },
  { value: "$10,000–$15,000", label: "$10,001–$15,000" },
  { value: "$15,000–$25,000", label: "$15,001–$25,000" },
  { value: "$25,000+", label: "$25,000+" }
];

// Full job type list
const JOB_TYPE_OPTIONS = [
  "Cleaning",
  "Countertops",
  "Drywall",
  "Electrical",
  "Flooring",
  "Handyman / General Repairs",
  "HVAC",
  "Landscaping",
  "Moving / Hauling",
  "Painting",
  "Pest Control",
  "Plumbing",
  "Pressure Washing",
  "Remodel / Restoration",
  "Roofing",
  "Tile"
];

// Sort options
const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "highest_value", label: "Highest Project Value" },
  { value: "lowest_value", label: "Lowest Project Value" },
  { value: "lowest_credits", label: "Lowest Credits Required" }
];

const LOCAL_STORAGE_KEY = "contractor_lead_filters";

// Helper to compare arrays (order-independent)
const arraysEqual = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

// Helper to get display label from job size value
const getJobSizeLabel = (value) => {
  const option = JOB_SIZE_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
};

export default function LeadsFilterBar({ 
  onFilterChange, 
  profileDefaults = {},
  savedPreferences = {},
  token,
  API,
  onProfileDefaultsUpdated
}) {
  const [selectedJobSizes, setSelectedJobSizes] = useState([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [sortBy, setSortBy] = useState("newest");
  const [showJobSizes, setShowJobSizes] = useState(false);
  const [showJobTypes, setShowJobTypes] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [currentProfileDefaults, setCurrentProfileDefaults] = useState(profileDefaults);
  const [skipDebouncedSave, setSkipDebouncedSave] = useState(false);

  // Update currentProfileDefaults when props change
  useEffect(() => {
    setCurrentProfileDefaults(profileDefaults);
  }, [profileDefaults]);

  // Check if current filters differ from profile defaults
  const filtersDifferFromDefaults = () => {
    const defaultSizes = currentProfileDefaults?.job_sizes || [];
    const defaultTypes = currentProfileDefaults?.job_types || [];
    
    return !arraysEqual(selectedJobSizes, defaultSizes) || 
           !arraysEqual(selectedJobTypes, defaultTypes);
  };

  // Initialize filters on mount
  useEffect(() => {
    if (!isInitialized) {
      initializeFilters();
    }
  }, [profileDefaults, savedPreferences, isInitialized]);

  const initializeFilters = () => {
    // Priority: localStorage > saved preferences > profile defaults
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSelectedJobSizes(parsed.job_sizes || []);
        setSelectedJobTypes(parsed.job_types || []);
        setSortBy(parsed.sort_by || "newest");
      } catch (e) {
        // Fall back to saved preferences or defaults
        applyFromSavedOrDefaults();
      }
    } else {
      // No localStorage - check saved preferences from database
      applyFromSavedOrDefaults();
    }
    
    setIsInitialized(true);
  };

  const applyFromSavedOrDefaults = () => {
    // Check if there are saved preferences (even empty ones mean "user cleared filters")
    if (savedPreferences && typeof savedPreferences === 'object' && 
        (Array.isArray(savedPreferences.job_sizes) || Array.isArray(savedPreferences.job_types))) {
      // User has saved preferences (could be empty from clearing)
      setSelectedJobSizes(savedPreferences.job_sizes || []);
      setSelectedJobTypes(savedPreferences.job_types || []);
      setSortBy(savedPreferences.sort_by || "newest");
    } else {
      // No saved preferences at all - use profile defaults for first-time users
      const defaultSizes = profileDefaults?.job_sizes || [];
      const defaultTypes = profileDefaults?.job_types || [];
      setSelectedJobSizes(defaultSizes);
      setSelectedJobTypes(defaultTypes);
      setSortBy("newest");
    }
  };

  // Persist and notify parent when filters change
  useEffect(() => {
    if (isInitialized) {
      const filters = {
        job_sizes: selectedJobSizes,
        job_types: selectedJobTypes,
        sort_by: sortBy
      };
      
      // Save to localStorage for instant restore
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filters));
      
      // Notify parent component
      onFilterChange(filters);
    }
  }, [selectedJobSizes, selectedJobTypes, sortBy, isInitialized]);

  // Save to database (debounced)
  useEffect(() => {
    if (isInitialized && token && API && !skipDebouncedSave) {
      const timer = setTimeout(() => {
        saveToDatabase();
      }, 1000); // Debounce 1 second
      
      return () => clearTimeout(timer);
    }
    // Reset the skip flag after a short delay
    if (skipDebouncedSave) {
      const resetTimer = setTimeout(() => setSkipDebouncedSave(false), 100);
      return () => clearTimeout(resetTimer);
    }
  }, [selectedJobSizes, selectedJobTypes, sortBy, isInitialized, skipDebouncedSave]);

  const saveToDatabase = async () => {
    try {
      await fetch(`${API}/contractor/lead-filter-preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          job_sizes: selectedJobSizes,
          job_types: selectedJobTypes,
          sort_by: sortBy
        })
      });
    } catch (error) {
      /* silent - error deliberately ignored */
    }
  };

  const toggleJobSize = (size) => {
    setSelectedJobSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const toggleJobType = (type) => {
    setSelectedJobTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const clearFilters = async () => {
    
    // Set flag to skip debounced save (we'll save manually)
    setSkipDebouncedSave(true);
    
    // Reset all selections to empty (not profile defaults)
    setSelectedJobSizes([]);
    setSelectedJobTypes([]);
    setSortBy("newest");
    
    // Clear localStorage immediately
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    
    // Clear database filter preferences
    if (token && API) {
      try {
        await fetch(`${API}/contractor/lead-filter-preferences`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            job_sizes: [],
            job_types: [],
            sort_by: "newest"
          })
        });
      } catch (error) {
      /* silent - error deliberately ignored */
    }
    }
    
    // Immediately notify parent to re-fetch with no filters
    // This ensures the query is sent without any filter constraints
    const clearedFilters = { job_sizes: [], job_types: [], sort_by: "newest" };
    onFilterChange(clearedFilters);
  };

  const saveAsDefault = async () => {
    if (!token || !API) return;
    
    setSavingDefaults(true);
    try {
      const response = await fetch(`${API}/contractor/profile-defaults`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          job_sizes: selectedJobSizes,
          job_types: selectedJobTypes
        })
      });
      
      if (response.ok) {
        // Update local state to reflect new defaults
        setCurrentProfileDefaults({
          job_sizes: selectedJobSizes,
          job_types: selectedJobTypes
        });
        
        // Notify parent component if callback provided
        if (onProfileDefaultsUpdated) {
          onProfileDefaultsUpdated({
            job_sizes: selectedJobSizes,
            job_types: selectedJobTypes
          });
        }
        
        toast.success("Default preferences updated");
      } else {
        toast.error("Failed to update preferences");
      }
    } catch (error) {
      toast.error("Failed to update preferences");
    } finally {
      setSavingDefaults(false);
    }
  };

  const hasActiveFilters = selectedJobSizes.length > 0 || selectedJobTypes.length > 0 || sortBy !== "newest";
  const totalFilters = selectedJobSizes.length + selectedJobTypes.length;
  const canSaveAsDefault = isInitialized && filtersDifferFromDefaults();

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6" data-testid="leads-filter-bar">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Filter Icon and Label */}
        <div className="flex items-center gap-2 text-[#1F2933]/70">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filters</span>
          {totalFilters > 0 && (
            <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] text-xs">
              {totalFilters} active
            </Badge>
          )}
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap gap-3 flex-1">
          {/* Job Size Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowJobSizes(!showJobSizes); setShowJobTypes(false); }}
              className={`h-9 ${selectedJobSizes.length > 0 ? 'border-[#0FA3B1] text-[#0FA3B1]' : ''}`}
              data-testid="job-size-filter-btn"
            >
              Job Size
              {selectedJobSizes.length > 0 && (
                <Badge className="ml-2 bg-[#0FA3B1] text-white text-xs px-1.5">
                  {selectedJobSizes.length}
                </Badge>
              )}
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showJobSizes ? 'rotate-180' : ''}`} />
            </Button>
            
            {showJobSizes && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-lg z-50 p-3" data-testid="job-size-dropdown">
                <p className="text-xs text-[#1F2933]/50 mb-2 font-medium">Select job sizes</p>
                <div className="space-y-1">
                  {JOB_SIZE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => toggleJobSize(option.value)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedJobSizes.includes(option.value)
                          ? 'bg-[#0FA3B1]/10 text-[#0FA3B1] font-medium'
                          : 'hover:bg-gray-50 text-[#1F2933]/80'
                      }`}
                      data-testid={`job-size-option-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between">
                  <button 
                    onClick={() => setSelectedJobSizes([])}
                    className="text-xs text-[#1F2933]/50 hover:text-[#1F2933]"
                  >
                    Clear
                  </button>
                  <button 
                    onClick={() => setShowJobSizes(false)}
                    className="text-xs text-[#0FA3B1] font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Job Type Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowJobTypes(!showJobTypes); setShowJobSizes(false); }}
              className={`h-9 ${selectedJobTypes.length > 0 ? 'border-[#0FA3B1] text-[#0FA3B1]' : ''}`}
              data-testid="job-type-filter-btn"
            >
              Job Type
              {selectedJobTypes.length > 0 && (
                <Badge className="ml-2 bg-[#0FA3B1] text-white text-xs px-1.5">
                  {selectedJobTypes.length}
                </Badge>
              )}
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showJobTypes ? 'rotate-180' : ''}`} />
            </Button>
            
            {showJobTypes && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl border border-gray-200 shadow-lg z-50 p-3 max-h-80 overflow-y-auto" data-testid="job-type-dropdown">
                <p className="text-xs text-[#1F2933]/50 mb-2 font-medium">Select job types</p>
                <div className="grid grid-cols-2 gap-1">
                  {JOB_TYPE_OPTIONS.map((type) => (
                    <button
                      key={type}
                      onClick={() => toggleJobType(type)}
                      className={`text-left px-2 py-1.5 rounded-lg text-xs transition-colors ${
                        selectedJobTypes.includes(type)
                          ? 'bg-[#0FA3B1]/10 text-[#0FA3B1] font-medium'
                          : 'hover:bg-gray-50 text-[#1F2933]/80'
                      }`}
                      data-testid={`job-type-option-${type}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between">
                  <button 
                    onClick={() => setSelectedJobTypes([])}
                    className="text-xs text-[#1F2933]/50 hover:text-[#1F2933]"
                  >
                    Clear
                  </button>
                  <button 
                    onClick={() => setShowJobTypes(false)}
                    className="text-xs text-[#0FA3B1] font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sort By Dropdown */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="sort-by-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Save as Default Button - only show when filters differ from profile defaults */}
          {canSaveAsDefault && (
            <Button
              variant="outline"
              size="sm"
              onClick={saveAsDefault}
              disabled={savingDefaults}
              className="text-[#0FA3B1] border-[#0FA3B1] hover:bg-[#0FA3B1]/10"
              data-testid="save-as-default-btn"
            >
              {savingDefaults ? (
                <>
                  <div className="w-3 h-3 border-2 border-[#0FA3B1]/20 border-t-[#0FA3B1] rounded-full animate-spin mr-1" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save as Default
                </>
              )}
            </Button>
          )}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-[#1F2933]/60 hover:text-[#1F2933]"
              data-testid="clear-filters-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Active Filter Tags */}
      {(selectedJobSizes.length > 0 || selectedJobTypes.length > 0) && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
          {selectedJobSizes.map((size) => (
            <Badge 
              key={size} 
              variant="secondary" 
              className="bg-[#0FA3B1]/10 text-[#0FA3B1] cursor-pointer hover:bg-[#0FA3B1]/20"
              onClick={() => toggleJobSize(size)}
            >
              {getJobSizeLabel(size)}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
          {selectedJobTypes.map((type) => (
            <Badge 
              key={type} 
              variant="secondary" 
              className="bg-purple-100 text-purple-700 cursor-pointer hover:bg-purple-200"
              onClick={() => toggleJobType(type)}
            >
              {type}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
